/**
 * System › Message Library — the Import file's rules, as pure functions.
 *
 * The preview an operator approves and the change set the server is sent both
 * come from here, so what the dialog promises and what is posted can never
 * disagree. Kept out of the component so each rule is testable on its own.
 */
import { normalizeKey } from './messageLibraryModel';
import type { MessageImportRow } from './api';
import type { MessageRow } from './types';

/** Why a file row was not applied. */
export type SkipReason = 'unknown-key' | 'empty-message' | 'unchanged';

/** What an Import file would do, worked out against the loaded rows. */
export interface ImportPreview {
  /** Rows in the file (excluding the header). */
  total: number;
  /** File rows whose key matched a live message. */
  matched: number;
  /** The changes that would actually be posted. */
  changes: MessageImportRow[];
  /** Keys in the file that match no live message. */
  unknownKeys: string[];
  /** Keys whose Message cell was blank. */
  emptyMessages: string[];
  /** Matched rows that were already identical. */
  unchanged: number;
}

/** The header the file must carry for a row to be identifiable. */
export const REQUIRED_COLUMN = 'MessageKey';

/** Does this parsed file have the column rows are matched on? */
export function hasRequiredColumn(parsed: Record<string, string>[]): boolean {
  return parsed.length > 0 && Object.keys(parsed[0]!).includes(REQUIRED_COLUMN);
}

/**
 * Works out what an Import file would change.
 *
 * The rules, matching what the server then enforces:
 *  - rows are matched on MessageKey, case-insensitively;
 *  - a key matching no live message is SKIPPED, not created — Import edits
 *    wording, it does not add messages;
 *  - a blank Message is SKIPPED rather than wiping the wording, because an empty
 *    spreadsheet cell almost never means "delete this";
 *  - a file with no Remark column leaves remarks alone, rather than clearing
 *    every one of them;
 *  - a row identical to what is stored produces no change and no audit entry.
 */
export function previewImport(
  parsed: Record<string, string>[], rows: MessageRow[],
): ImportPreview {
  const byKey = new Map<string, MessageRow>();
  for (const r of rows) {
    const k = normalizeKey(r.messageKey);
    // A duplicate key cannot be targeted unambiguously, so the first wins and
    // the other is left untouched — the same rule the server applies.
    if (k && !byKey.has(k)) byKey.set(k, r);
  }

  const hasRemarkColumn = parsed.length > 0 && Object.keys(parsed[0]!).includes('Remark');

  const changes: MessageImportRow[] = [];
  const unknownKeys: string[] = [];
  const emptyMessages: string[] = [];
  let matched = 0;
  let unchanged = 0;

  for (const line of parsed) {
    const rawKey = (line[REQUIRED_COLUMN] ?? '').trim();
    const existing = byKey.get(normalizeKey(rawKey));
    if (!existing) {
      if (rawKey) unknownKeys.push(rawKey);
      continue;
    }
    matched++;

    const message = (line.Message ?? '').trim();
    if (!message) {
      emptyMessages.push(rawKey);
      continue;
    }

    const remark = hasRemarkColumn ? ((line.Remark ?? '').trim() || null) : undefined;

    const messageChanged = message !== (existing.message ?? '').trim();
    const remarkChanged = remark !== undefined
      && (remark ?? '') !== (existing.remark ?? '').trim();

    if (!messageChanged && !remarkChanged) { unchanged++; continue; }

    changes.push({
      messageKey: rawKey,
      message,
      // undefined (no column) is omitted so the server leaves the remark alone;
      // null (blank cell) explicitly clears it.
      ...(remark === undefined ? {} : { remark }),
    });
  }

  return { total: parsed.length, matched, changes, unknownKeys, emptyMessages, unchanged };
}

/** The Template / export rows for the current grid, in the import column order. */
export function templateRows(rows: MessageRow[]): string[][] {
  return rows.map((r) => [
    r.msid ?? '',
    r.messageKey ?? '',
    r.message ?? '',
    r.remark ?? '',
  ]);
}
